import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CalendarMonth,
  People,
  Schedule,
  Dashboard,
  ArrowForward,
  CalendarViewMonth as LogoIcon,
  Login,
  Speed,
  Devices,
  Security,
  Support,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

// Placeholder image URLs - replace with actual images later
const IMAGES = {
  hero: "images/landing/hero.jpeg",
  scheduling: "images/landing/scheduling.jpeg",
  availability: "images/landing/availability.jpeg",
  dashboard: "images/landing/dashboard.jpeg",
  employeePortal: "images/landing/employeePortal.jpeg",
};

const features = [
  {
    icon: <CalendarMonth sx={{ fontSize: 40 }} />,
    title: "Smart Scheduling",
    description:
      "Create and manage shifts effortlessly. Copy/Paste interface makes scheduling a breeze.",
    image: IMAGES.scheduling,
    color: "#0891b2", // Cyan - primary
  },
  {
    icon: <Schedule sx={{ fontSize: 40 }} />,
    title: "Availability Management",
    description:
      "Employees set their availability. You see it all in one place. No more back-and-forth.",
    image: IMAGES.availability,
    color: "#0e7490", // Cyan dark
  },
  {
    icon: <Dashboard sx={{ fontSize: 40 }} />,
    title: "Powerful Dashboard",
    description:
      "Get insights at a glance. Track hours, coverage, and team performance.",
    image: IMAGES.dashboard,
    color: "#d97706", // Amber - warning accent
  },
  {
    icon: <People sx={{ fontSize: 40 }} />,
    title: "Employee Portal",
    description:
      "Employees can view schedules, update availability, and manage their shifts from anywhere.",
    image: IMAGES.employeePortal,
    color: "#f97316", // Orange - secondary
  },
];

const steps = [
  {
    number: "1",
    title: "Set Up Your Team",
    description:
      "Add your shops and invite employees. They can join with a simple link.",
  },
  {
    number: "2",
    title: "Collect Availability",
    description:
      "Employees enter when they can work. See everyone's availability in real-time.",
  },
  {
    number: "3",
    title: "Create Schedules",
    description:
      "Build schedules knowing who's available. Publish and notify your team instantly.",
  },
];

const benefits = [
  {
    icon: <Speed />,
    title: "Save Time",
    description:
      "Reduce scheduling time by up to 80% with our intuitive tools.",
  },
  {
    icon: <Devices />,
    title: "Works Everywhere",
    description: "Access from any device - desktop, tablet, or mobile.",
  },
  {
    icon: <Security />,
    title: "Secure & Reliable",
    description: "Your data is encrypted and backed up automatically.",
  },
  {
    icon: <Support />,
    title: "Easy to Use",
    description: "No training needed. Get started in minutes, not hours.",
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const handleGetStarted = () => {
    if (user) {
      // If logged in, go to appropriate dashboard
      if (user.role === "employee") {
        navigate("/employee/dashboard");
      } else {
        navigate("/dashboard");
      }
    } else {
      navigate("/auth");
    }
  };

  const handleLogin = () => {
    navigate("/auth");
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh" }}>
      {/* Navigation Bar */}
      <Box
        component="nav"
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1100,
          bgcolor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              py: 1.5,
            }}
          >
            {/* Logo */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <LogoIcon />
              </Box>
              <Typography variant="h5" fontWeight={700} color="primary.main">
                ShiftGrid
              </Typography>
            </Box>

            {/* CTA Buttons */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              {user ? (
                <Button
                  variant="contained"
                  onClick={handleGetStarted}
                  endIcon={<ArrowForward />}
                >
                  Go to Dashboard
                </Button>
              ) : (
                <>
                  {!isSmall && (
                    <Button
                      variant="text"
                      onClick={handleLogin}
                      startIcon={<Login />}
                    >
                      Login
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    onClick={handleGetStarted}
                    endIcon={<ArrowForward />}
                  >
                    {isSmall ? "Start" : "Get Started"}
                  </Button>
                </>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Hero Section */}
      <Box
        sx={{
          pt: { xs: 12, md: 16 },
          pb: { xs: 8, md: 12 },
          background: "linear-gradient(180deg, #f5f9ff 0%, #ffffff 100%)",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box sx={{ pr: { md: 4 } }}>
                <Typography
                  variant="h1"
                  sx={{
                    fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" },
                    fontWeight: 800,
                    lineHeight: 1.2,
                    mb: 2,
                    background:
                      "linear-gradient(135deg, #0891b2 0%, #22d3ee 100%)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                  }}
                >
                  Shift Scheduling Made Simple
                </Typography>
                <Typography
                  variant="h5"
                  color="text.secondary"
                  sx={{
                    mb: 4,
                    fontWeight: 400,
                    fontSize: { xs: "1.1rem", md: "1.25rem" },
                    lineHeight: 1.6,
                  }}
                >
                  Stop wasting time on spreadsheets. ShiftGrid helps you create
                  schedules, track availability, and keep your team in sync -
                  all in one place.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleGetStarted}
                    endIcon={<ArrowForward />}
                    sx={{
                      py: 1.5,
                      px: 4,
                      fontSize: "1.1rem",
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "0 4px 14px rgba(5, 150, 105, 0.4)",
                    }}
                  >
                    Get Started Free
                  </Button>
                  {!user && (
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={handleLogin}
                      sx={{
                        py: 1.5,
                        px: 4,
                        fontSize: "1.1rem",
                        borderRadius: 2,
                        textTransform: "none",
                      }}
                    >
                      Sign In
                    </Button>
                  )}
                </Stack>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)",
                }}
              >
                <Box
                  component="img"
                  src={IMAGES.hero}
                  alt="Team collaboration"
                  sx={{
                    width: "100%",
                    height: "auto",
                    display: "block",
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Features Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "white" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: "primary.main",
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: "block",
              }}
            >
              Features
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Everything You Need
            </Typography>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ maxWidth: 600, mx: "auto", fontSize: "1.1rem" }}
            >
              Powerful features designed to make scheduling effortless for you
              and your team.
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {features.map((feature) => (
              <Grid item xs={12} md={6} key={feature.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    height: "100%",
                    borderRadius: 4,
                    border: "1px solid",
                    borderColor: "grey.200",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: feature.color,
                      boxShadow: `0 8px 30px ${feature.color}20`,
                      transform: "translateY(-4px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: { xs: "column", sm: "row" },
                      gap: 3,
                    }}
                  >
                    <Box sx={{ flex: "0 0 auto" }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 3,
                          bgcolor: `${feature.color}15`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: feature.color,
                          mb: { xs: 2, sm: 0 },
                        }}
                      >
                        {feature.icon}
                      </Box>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                        {feature.title}
                      </Typography>
                      <Typography color="text.secondary" sx={{ mb: 2 }}>
                        {feature.description}
                      </Typography>
                      <Box
                        sx={{
                          borderRadius: 2,
                          overflow: "hidden",
                          border: "1px solid",
                          borderColor: "grey.200",
                        }}
                      >
                        <Box
                          component="img"
                          src={feature.image}
                          alt={feature.title}
                          sx={{
                            width: "100%",
                            height: "auto",
                            display: "block",
                          }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* How It Works Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          bgcolor: "grey.50",
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: "primary.main",
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: "block",
              }}
            >
              How It Works
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Get Started in 3 Steps
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {steps.map((step, index) => (
              <Grid item xs={12} md={4} key={step.number}>
                <Box
                  sx={{
                    textAlign: "center",
                    position: "relative",
                  }}
                >
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      bgcolor: "primary.main",
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 3,
                      fontSize: "2rem",
                      fontWeight: 700,
                      boxShadow: "0 4px 20px rgba(5, 150, 105, 0.3)",
                    }}
                  >
                    {step.number}
                  </Box>
                  {/* Connector line (desktop only) */}
                  {index < steps.length - 1 && !isMobile && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 40,
                        left: "60%",
                        width: "80%",
                        height: 2,
                        bgcolor: "primary.light",
                        opacity: 0.3,
                      }}
                    />
                  )}
                  <Typography variant="h5" fontWeight={600} sx={{ mb: 1 }}>
                    {step.title}
                  </Typography>
                  <Typography
                    color="text.secondary"
                    sx={{ maxWidth: 280, mx: "auto" }}
                  >
                    {step.description}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Benefits Section */}
      <Box sx={{ py: { xs: 8, md: 12 }, bgcolor: "white" }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: "center", mb: { xs: 6, md: 8 } }}>
            <Typography
              variant="overline"
              sx={{
                color: "primary.main",
                fontWeight: 600,
                letterSpacing: 2,
                mb: 1,
                display: "block",
              }}
            >
              Benefits
            </Typography>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Why Choose ShiftGrid?
            </Typography>
          </Box>

          <Grid container spacing={3}>
            {benefits.map((benefit) => (
              <Grid item xs={12} sm={6} md={3} key={benefit.title}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    textAlign: "center",
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "grey.200",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: "0 4px 20px rgba(5, 150, 105, 0.15)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: 2,
                      bgcolor: "primary.50",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                      color: "primary.main",
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
          color: "white",
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: "center" }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.5rem" },
                fontWeight: 700,
                mb: 2,
              }}
            >
              Ready to Simplify Your Scheduling?
            </Typography>
            <Typography
              variant="h6"
              sx={{
                mb: 4,
                opacity: 0.9,
                fontWeight: 400,
                maxWidth: 500,
                mx: "auto",
              }}
            >
              Join businesses that have transformed their shift management with
              ShiftGrid.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={handleGetStarted}
              endIcon={<ArrowForward />}
              sx={{
                py: 1.5,
                px: 5,
                fontSize: "1.1rem",
                borderRadius: 2,
                textTransform: "none",
                bgcolor: "white",
                color: "primary.main",
                "&:hover": {
                  bgcolor: "grey.100",
                },
              }}
            >
              {user ? "Go to Dashboard" : "Get Started Free"}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          py: 4,
          bgcolor: "grey.900",
          color: "grey.400",
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: 1.5,
                  bgcolor: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                }}
              >
                <LogoIcon fontSize="small" />
              </Box>
              <Typography variant="body1" fontWeight={600} color="white">
                ShiftGrid
              </Typography>
            </Box>
            <Typography
              variant="body2"
              sx={{ textAlign: { xs: "center", md: "right" } }}
            >
              &copy; {new Date().getFullYear()} ShiftGrid. All rights reserved.
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
}
